import { useId, type SVGProps } from 'react';
import { palette } from '../tokens.ts';

/**
 * Line engraving of a brig riding at anchor with sails furled. Stroke only, no fill
 * except engraved hatching. Placeholder art for empty states.
 */
export function ShipAtAnchor({
  color = palette.ink,
  title,
  ...rest
}: SVGProps<SVGSVGElement> & { color?: string; title?: string }) {
  const id = useId().replace(/:/g, '');

  // Furled sail along a yard: a row of small scallops.
  const furled = (x1: number, x2: number, y: number) => {
    const steps = Math.max(2, Math.round((x2 - x1) / 7));
    const w = (x2 - x1) / steps;
    let d = `M${x1} ${y}`;
    for (let i = 0; i < steps; i += 1) d += ` q${w / 2} 4 ${w} 0`;
    return d;
  };

  return (
    <svg
      viewBox="0 0 320 210"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <pattern id={`${id}-hatch`} width="3" height="3" patternUnits="userSpaceOnUse">
          <line x1="0" y1="1.5" x2="3" y2="1.5" stroke={color} strokeWidth="0.7" />
        </pattern>
        <pattern
          id={`${id}-cross`}
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(35)"
        >
          <line x1="0" y1="2" x2="4" y2="2" stroke={color} strokeWidth="0.5" />
        </pattern>
      </defs>

      <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
        {/* Hull with engraved shading */}
        <path
          d="M46 140 C80 156 222 156 268 136 L262 124 C200 130 110 130 52 124 Z"
          fill={`url(#${id}-hatch)`}
          strokeWidth={1.3}
        />
        <path d="M52 124 C110 130 200 130 262 124" strokeWidth={0.8} />
        <path d="M58 132 C112 138 198 138 258 131" strokeWidth={0.5} />
        {/* Gun ports */}
        {[80, 100, 120, 140, 160, 180, 200, 220].map((x) => (
          <rect key={x} x={x} y={128.5} width={5} height={4} fill={palette.paper} strokeWidth={0.6} />
        ))}
        {/* Stern lantern & rudder */}
        <path d="M46 140 L44 148" strokeWidth={1} />
        <path d="M50 120 L50 124" strokeWidth={0.8} />
        <circle cx={50} cy={118} r={2} strokeWidth={0.6} />

        {/* Bowsprit & jib-boom */}
        <path d="M262 126 L310 108" strokeWidth={1.4} />
        <path d="M296 113 L316 105" strokeWidth={0.8} />

        {/* Masts */}
        <path d="M130 128 V44" strokeWidth={2} />
        <path d="M130 50 V22" strokeWidth={1.2} />
        <path d="M214 128 V52" strokeWidth={2} />
        <path d="M214 58 V30" strokeWidth={1.2} />
        {/* Tops */}
        <path d="M122 76 H138 M206 82 H222" strokeWidth={1.2} />

        {/* Yards with furled sails */}
        {[
          [104, 156, 104],
          [110, 150, 80],
          [116, 144, 56],
          [120, 140, 36],
          [188, 240, 110],
          [194, 234, 86],
          [200, 228, 64],
          [204, 224, 42],
        ].map(([x1 = 0, x2 = 0, y = 0]) => (
          <g key={`${x1}-${y}`}>
            <path d={`M${x1} ${y} H${x2}`} strokeWidth={1.1} />
            <path d={furled(x1 + 2, x2 - 2, y + 1)} strokeWidth={0.7} />
          </g>
        ))}

        {/* Spanker gaff and boom on the mainmast */}
        <path d="M130 62 L88 74" strokeWidth={1} />
        <path d="M130 116 L70 120" strokeWidth={1} />
        <path d="M88 74 C84 90 78 106 72 119" strokeWidth={0.5} strokeDasharray="2 2" />

        {/* Standing rigging */}
        <g strokeWidth={0.5}>
          <path d="M130 22 L52 124" />
          <path d="M130 30 L214 52" />
          <path d="M214 30 L316 105" />
          <path d="M214 44 L296 113" />
          <path d="M214 58 L262 126" />
          {[0, 6, 12].map((dx) => (
            <path key={`ms${dx}`} d={`M130 76 L${112 + dx} 128`} />
          ))}
          {[0, 6, 12].map((dx) => (
            <path key={`mp${dx}`} d={`M130 76 L${136 + dx} 128`} />
          ))}
          {[0, 6, 12].map((dx) => (
            <path key={`fs${dx}`} d={`M214 82 L${196 + dx} 127`} />
          ))}
          {[0, 6, 12].map((dx) => (
            <path key={`fp${dx}`} d={`M214 82 L${220 + dx} 126`} />
          ))}
        </g>

        {/* Masthead pennant */}
        <path d="M130 22 L150 26 L130 30" strokeWidth={0.8} fill={palette.sealRed} />

        {/* Anchor cable to the sea bed */}
        <path d="M264 132 C276 146 284 160 292 176" strokeWidth={0.9} strokeDasharray="3 1.5" />

        {/* Sea: engraved horizontal strokes, denser near the hull */}
        <g strokeWidth={0.6}>
          {Array.from({ length: 12 }, (_, i) => {
            const y = 150 + i * 4.5;
            const inset = Math.max(0, 40 - i * 4);
            const left = 10 + ((i * 29) % 30) + inset;
            const right = 310 - ((i * 17) % 34) - inset / 2;
            return <path key={y} d={`M${left} ${y} H${right}`} opacity={1 - i * 0.06} />;
          })}
        </g>
        {/* Reflection */}
        <path d="M70 158 H110 M150 160 H210 M100 166 H170" strokeWidth={1.1} opacity={0.7} />
        <rect x={90} y={150} width={140} height={10} fill={`url(#${id}-cross)`} stroke="none" opacity={0.6} />

        {/* Distant gulls */}
        <path d="M60 50 q4 -4 8 0 q4 -4 8 0" strokeWidth={0.7} />
        <path d="M250 24 q3 -3 6 0 q3 -3 6 0" strokeWidth={0.6} />
      </g>
    </svg>
  );
}
